/**
 * Агенты резервного копирования сайтов: кто есть, что делает прямо сейчас.
 *
 * Экран живой. Ход прохода приходит событиями в топик `agents`, а не
 * опросом: проход по медиатеке идёт часами, и владелец должен видеть, что он
 * движется, а не гадать по неизменной странице, жив ли агент.
 *
 * Ключи выдаются и отзываются отсюда же. Разовый код показывается ОДИН раз:
 * в базе лежит только его хеш, и второй раз консоль его не покажет, потому
 * что не может.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { useTopic } from '../transport/live.ts';
import { request } from '../transport/socket.ts';
import { ago, agoPhrase, bytes, count, duration, plural, until } from '../lib/format.ts';
import { forHuman, useEased, useFlowInertia, useRecentRate, windowedRate } from '../lib/rate.ts';
import type {
  AgentEvent, AgentRunView, AgentsSnapshot, AgentView, HostReportView, IssuedTokenView,
  ReportedSite,
} from '../../shared/protocol/agents.ts';
import { AGENTS_TOPIC } from '../../shared/protocol/agents.ts';
import { Confirm, useConfirm } from '../components/Confirm.tsx';
import { Toggle } from '../components/Toggle.tsx';
import { SpeedGauge } from '../components/SpeedGauge.tsx';
import { EditName } from '../components/EditName.tsx';

const PHASE_WORD: Record<AgentRunView['phase'], string> = {
  scan: 'обходит файлы',
  read: 'читает и режет',
  upload: 'грузит в хранилище',
  finalize: 'дописывает манифест',
};

const REASON_WORD: Record<AgentRunView['reason'], string> = {
  schedule: 'по расписанию',
  manual: 'вручную',
  retry: 'повтор',
};

/**
 * Полоса хода прохода.
 *
 * Доля считается против ОЖИДАЕМОГО, а не против увиденного. Долго было
 * наоборот — `filesDone / filesSeen`, — и на экране всегда стояло «Х из Х»:
 * у агента обход и чтение не разделены, файл читается сразу как встретился, и
 * два числа отличаются на единицы. Из них полосы не выходит, сколько ни
 * считай.
 *
 * Ожидаемое берётся из замера объёма, снятого до прохода. Замер бывает не
 * всегда — тогда бегущая штриховка, и это честнее доли от неизвестного.
 *
 * Для дампа базы ожидаемое даётся в байтах, а не в файлах: файл там один.
 * Число это ОЦЕНОЧНОЕ — размер базы по information_schema против размера
 * дампа расходится в полтора раза, — поэтому такая полоса подписана оценкой.
 */


/**
 * Сводка сверху: сколько сейчас читается и отдаётся ВСЕМИ агентами разом.
 *
 * Отдельно от карточек, потому что отвечает на другой вопрос. Карточка говорит,
 * что делает одна машина; сводка — загружено ли хозяйство в целом и стоит ли
 * ждать. Складывать её глазами по четырём карточкам владелец не должен.
 *
 * Складываются только ИДУЩИЕ проходы: законченный проход скорости не имеет, а
 * его последние числа в сумме означали бы работу, которой нет.
 */
/**
 * Скорость сводки: каждый проход считается ОТДЕЛЬНО, складываются скорости.
 *
 * Раньше складывались счётчики всех проходов и делились на время. Пока набор
 * проходов не меняется, это одно и то же; но он меняется. Закончился проход —
 * общее прочитанное падает на его объём, разность выходит отрицательной,
 * обрезается нулём, и сводка показывает штиль при работающих соседях — все
 * двадцать секунд, пока провал не выйдет из окна. Появился новый — наоборот,
 * его накопленное влетает в окно скачком и завышает скорость.
 *
 * Отсюда и бралось отставание сводки от карточек: карточка считает свой проход
 * и на чужие не смотрит.
 *
 * Готовые числа агента берём, только когда их шлют ВСЕ идущие: сумма из
 * половины была бы скоростью не того хозяйства.
 */
function useTotalRate(live: AgentView[]): { read: number; sent: number } {
  const hist = useRef<Map<string, { t: number; read: number; sent: number }[]>>(new Map());
  const [rate, setRate] = useState<{ read: number; sent: number }>({ read: 0, sent: 0 });

  const readTotal = live.reduce((n, a) => n + a.currentRun!.progress.bytesRead, 0);
  const sentTotal = live.reduce((n, a) => n + a.currentRun!.progress.bytesUploaded, 0);
  const runs = live.map((a) => a.currentRun!.runId).join('|');

  useEffect(() => {
    if (live.length > 0 && live.every((a) => a.currentRun!.progress.readPerSec != null)) {
      setRate({
        read: live.reduce((n, a) => n + (a.currentRun!.progress.readPerSec ?? 0), 0),
        sent: live.reduce((n, a) => n + (a.currentRun!.progress.uploadedPerSec ?? 0), 0),
      });
      return;
    }

    const now = Date.now();
    const alive = new Set<string>();
    for (const a of live) {
      const r = a.currentRun!;
      alive.add(r.runId);
      const h = hist.current.get(r.runId) ?? [];
      h.push({ t: now, read: r.progress.bytesRead, sent: r.progress.bytesUploaded });
      if (h.length > 40) h.shift();
      hist.current.set(r.runId, h);
    }
    // Ушедший проход просто перестаёт учитываться. Не «его скорость упала до
    // нуля», а «его больше нет»: это разные вещи, и вторая не должна попадать
    // в среднее.
    for (const id of [...hist.current.keys()]) if (!alive.has(id)) hist.current.delete(id);

    let read = 0;
    let sent = 0;
    for (const h of hist.current.values()) {
      const r = windowedRate(h);
      if (r) { read += r.read; sent += r.sent; }
    }
    setRate({ read, sent });
  }, [readTotal, sentTotal, runs]);

  return rate;
}

function TotalSpeed({ agents }: { agents: AgentView[] }): React.JSX.Element | null {
  // Именно идущие. Законченный проход остаётся в карточке до следующего, и,
  // попадая в сводку, он и заголовок завышал, и скорость занижал своим нулём.
  const live = agents.filter((a) => a.currentRun && a.currentRun.state === 'running');

  const readTotal = live.reduce((n, a) => n + a.currentRun!.progress.bytesRead, 0);
  const sentTotal = live.reduce((n, a) => n + a.currentRun!.progress.bytesUploaded, 0);
  const files = live.reduce((n, a) => n + a.currentRun!.progress.filesDone, 0);
  const chunksTotal = live.reduce((n, a) => n + a.currentRun!.progress.chunksTotal, 0);
  const chunksNewTotal = live.reduce((n, a) => n + a.currentRun!.progress.chunksNew, 0);
  // Складываем, только если объём различного шлют ВСЕ идущие: сумма из
  // половины проходов означала бы долю, посчитанную не от того целого.
  const distinctTotal = live.every((a) => a.currentRun!.progress.bytesDistinct != null)
    ? live.reduce((n, a) => n + (a.currentRun!.progress.bytesDistinct ?? 0), 0)
    : null;

  const rate = useTotalRate(live);

  const easedRead = useEased(rate.read, live.length > 0);
  const easedSent = useEased(rate.sent, live.length > 0);

  if (live.length === 0) return null;

  return (
    <div className="totals">
      {/* Мини-спидометр той же формы, что и в карточке: одна привычка на два
          места, и глаз не переучивается. */}
      {/*
        В сводке те же дуги, но сложенные по всем проходам: доля совпавшего и
        доля улёгшегося в хранилище считаются от общего прочитанного, а не
        усредняются по агентам. Среднее из долей — величина, не значащая
        ничего: у прохода на гигабайт и прохода на терабайт вес разный.
      */}
      <SpeedGauge
        read={easedRead}
        sent={easedSent}
        matched={readTotal > 0 && distinctTotal != null
          ? 1 - distinctTotal / readTotal
          : chunksTotal > 0 ? 1 - chunksNewTotal / chunksTotal : null}
        stored={readTotal > 0 ? sentTotal / readTotal : null}
      />

      <div className="totals__right">
        <div className="totals__facts">
        <span className="totals__title">
          {live.length === 1 ? 'идёт один проход' : `идут ${live.length} прохода`}
          {live.length > 1 && (
            <span className="muted"> · {live.map((a) => a.currentRun!.siteId).join(', ')}</span>
          )}
        </span>
        <span className="totals__pair">
          <b>{bytes(readTotal)}</b>
          <span className="muted">прочитано · {bytes(sentTotal)} залито</span>
        </span>
        <span className="totals__pair">
          <b>{count(files)}</b>
          <span className="muted">файлов за проходы</span>
        </span>
        </div>

        {/*
          Общая доля — сбоку от прибора, а не над ним: сверху она добавила бы
          сводке высоту, которой в шапке нет, а справа место пустовало.

          Складываем сделанное и ожидаемое, а не усредняем доли, и только если
          знаменатель известен у ВСЕХ идущих. Доля, посчитанная от половины
          проходов, — это доля не от того целого.
        */}
        {(() => {
          const all = live.map((a) => a.currentRun!);
          const byFiles = all.every((r) => r.progress.filesTotal != null && r.progress.filesTotal > 0);
          const byBytes = !byFiles
            && all.every((r) => r.progress.bytesTotal != null && r.progress.bytesTotal > 0);
          const known = (byFiles || byBytes) && all.every((r) => r.phase !== 'scan');
          const done = byFiles
            ? all.reduce((n, r) => n + r.progress.filesDone, 0)
            : all.reduce((n, r) => n + r.progress.bytesRead, 0);
          const total = byFiles
            ? all.reduce((n, r) => n + (r.progress.filesTotal ?? 0), 0)
            : all.reduce((n, r) => n + (r.progress.bytesTotal ?? 0), 0);
          const share = known && total > 0 ? Math.min(1, done / total) : 0;
          return (
            <div className="totals__progress">
              <div className="run__bar" role="img"
                   aria-label={known ? `пройдено ${Math.round(share * 100)} процентов`
                     : 'доля пока неизвестна'}>
                {known
                  ? <span className="run__fill" style={{ width: `${Math.round(share * 100)}%` }} />
                  : <span className="run__fill run__fill--indeterminate" />}
              </div>
              <span className="totals__share">
                {known ? `пройдено ${Math.round(share * 100)}%`
                  : <span className="muted">доля пока неизвестна</span>}
              </span>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

/**
 * Когда закончится — и почему это оценка, а не обещание.
 *
 * Считаем по тому, чего больше знаем: если известно ожидаемое число файлов,
 * идём по файлам, иначе по байтам. Скорость берём СРЕДНЮЮ с начала, а не
 * текущую: текущая на медиатеке скачет вдесятеро, и прогноз по ней прыгал бы
 * с часа на сутки и обратно, обесценивая сам себя.
 *
 * `null` там, где считать не из чего. Показать «осталось 0» на проходе, у
 * которого нет замера, — это соврать в самую приятную сторону.
 */
function forecast(run: AgentRunView): { secs: number; basis: 'файлам' | 'байтам' } | null {
  const p = run.progress;
  const elapsed = (Date.now() - new Date(run.startedAt).getTime()) / 1000;
  // Первая минута ничего не предсказывает: скорость ещё не установилась.
  if (elapsed < 60) return null;

  if (p.filesTotal != null && p.filesTotal > 0 && p.filesDone > 0) {
    const left = p.filesTotal - p.filesDone;
    if (left <= 0) return null;
    return { secs: (left / (p.filesDone / elapsed)), basis: 'файлам' };
  }
  if (p.bytesTotal != null && p.bytesTotal > 0 && p.bytesRead > 0) {
    const left = p.bytesTotal - p.bytesRead;
    if (left <= 0) return null;
    return { secs: (left / (p.bytesRead / elapsed)), basis: 'байтам' };
  }
  return null;
}




/**
 * Скорость прохода — та, что получается на самом деле.
 *
 * Считается двумя числами, и оба нужны. «Читает» — сколько данных агент
 * прожевал: обход, чтение, нарезка, хеширование. «Льёт» — сколько ушло в
 * хранилище. Разрыв между ними и есть польза дедупликации: прочитали
 * гигабайт, залили сто мегабайт — значит девятьсот уже лежали.
 *
 * Среднее с начала прохода, а не мгновенное. Мгновенное скачет: кадры хода
 * приходят неровно, а пак закрывается на 64 МБ — «залито» стоит на месте и
 * прыгает разом. Среднее отвечает на вопрос, который и задают: успеет ли
 * проход до утра.
 */
function speeds(run: AgentRunView): { read: number; sent: number; secs: number } | null {
  const started = new Date(run.startedAt).getTime();
  const until = run.finishedAt ? new Date(run.finishedAt).getTime() : Date.now();
  const secs = (until - started) / 1000;
  // Меньше пяти секунд — числа бессмысленны: первый кадр придёт позже, чем
  // закончится этот отрезок, и получится «12 ГБ/с».
  if (secs < 5) return null;
  return {
    read: run.progress.bytesRead / secs,
    sent: run.progress.bytesUploaded / secs,
    secs,
  };
}

function RunProgress({ run, onCancel, busy }: {
  run: AgentRunView;
  onCancel?: (runId: string, siteId: string) => void;
  busy?: boolean;
}): React.JSX.Element {
  const p = run.progress;
  const average = speeds(run);
  const recent = useRecentRate(run.progress);

  // Часы, чтобы «идёт N с», молчание и доведение чисел не застывали между
  // сообщениями агента. Две секунды — тот же шаг, с каким он их шлёт.
  const [, tick] = useState(0);
  useEffect(() => {
    if (run.state !== 'running') return;
    const id = setInterval(() => tick((n) => n + 1), 2000);
    return () => clearInterval(id);
  }, [run.state]);

  const silentFor = Date.now() - new Date(run.lastActivityAt).getTime();
  // Доведение прекращается на десяти секундах тишины: дальше оно показывало бы
  // движение там, где его нет.
  const live = run.state === 'running' && silentFor < 10_000;

  const byFiles = p.filesTotal != null && p.filesTotal > 0;
  const byBytes = !byFiles && p.bytesTotal != null && p.bytesTotal > 0;
  // Замер объёма бывает вчерашним: файлов с тех пор стало больше, и доля
  // перевалила бы за сто процентов. Показываем как есть, но не выше единицы, а
  // саму оценку помечаем устаревшей — врать в сторону «почти готово» на
  // длинном проходе хуже, чем признать, что оценка неточна.
  const overrun = byFiles && p.filesDone > p.filesTotal!;
  const share = byFiles
    ? Math.min(1, p.filesDone / p.filesTotal!)
    : byBytes
      ? Math.min(1, p.bytesRead / p.bytesTotal!)
      : 0;

  const easedShare = useEased(share, live);
  const easedRead = useEased(p.bytesRead, live);
  const easedSent = useEased(p.bytesUploaded, live);
  const easedFiles = useEased(p.filesDone, live);
  const easedRate = useEased(recent?.read ?? 0, live);
  const easedRateSent = useEased(recent?.sent ?? 0, live);

  // Граница спидометра. Считается из НЕсглаженных чисел — доля это отношение,
  // и сглаживать её из уже сглаженных частей значит получить третье число, не
  // равное ни одной из них. А вот саму долю ведём с медленной инерцией, чтобы
  // цвета успевали переливаться, а не мигали от края к краю.
  // Файлов в секунду — среднее с начала: мгновенное по кадрам скачет вдесятеро
  // и ни на один вопрос не отвечает.
  const elapsedSecs = (Date.now() - new Date(run.startedAt).getTime()) / 1000;
  const avgFilesPerSec = elapsedSecs > 5 && p.filesDone > 0 ? p.filesDone / elapsedSecs : null;

  const sentShare = recent && recent.read > 0
    ? Math.min(1, Math.max(0, recent.sent / recent.read))
    : 0;
  // Сила инерции подбирается сама: мечется доля — тормозим, идёт ровно — нет.
  const pull = useFlowInertia(sentShare);
  const flow = useEased(1 - sentShare, live, pull);
  const known = (byFiles || byBytes) && run.phase !== 'scan';
  // Агент ещё ни одной цифры не прислал. У большой медиатеки обход идёт
  // минутами, и всё это время числа честнее не показывать вовсе, чем
  // показывать нули.
  const silent = p.filesSeen === 0 && p.bytesRead === 0;
  // Порог сторожа — 300 с. Предупреждаем заранее, а не по факту закрытия.
  const stale = silentFor > 180_000;

  return (
    <div className="run">
      <div className="run__head">
        <span className="run__site">{run.siteId}</span>
        <span className="run__phase">{PHASE_WORD[run.phase]}</span>
        <span className="run__since">идёт {duration(run.startedAt)}</span>
        {/*
          Прогноз рядом с «идёт», а не вместо: одно говорит, сколько уже
          потрачено, другое — сколько осталось, и владельцу нужны оба. Слово
          «примерно» не украшение: оценка по средней скорости, и на длинном
          проходе она заметно гуляет.
        */}
        {(() => {
          const eta = forecast(run);
          return eta ? (
            <span className="run__eta" title={`Оценка по ${eta.basis}, из средней скорости с начала прохода`}>
              осталось примерно {forHuman(eta.secs)}
            </span>
          ) : null;
        })()}
        {/*
          Остановка есть, но закрывает проход АГЕНТ, а не мы. Пометить его
          «остановлен» отсюда значило бы показать законченным то, что
          продолжает писать в хранилище.
        */}
        {onCancel && (
          <button
            type="button"
            className="button button--ghost button--small button--danger"
            disabled={busy}
            onClick={() => onCancel(run.runId, run.siteId)}
            title="Попросить агента прекратить проход. Уже залитое останется"
          >
            Остановить
          </button>
        )}
      </div>

      {/*
        Ноль и «ещё не сказали» — разные вещи, и показывать их одинаково
        нельзя. «0 из 0» читается как поломка, хотя означает, что агент пока
        считает файлы и цифр ещё не присылал.
      */}

      {/*
        Полоса делится на три, а не на две.
        «Льёт 4 МБ/с» рядом с «читает 111» выглядит затором, а это работающая
        дедупликация: 107 из 111 не понадобилось отправлять. Разделив, мы
        показываем два РАЗНЫХ случая по-разному — если различного почти столько
        же, сколько прочитанного, а уходит заметно меньше, вот тогда узкое
        место в сети и вот тогда стоит смотреть.
      */}
      <div className="run__gauge">
        {/*
          Спидометр слева, всё остальное справа от него — одной полосой.
          Раньше блоки шли друг под другом, и карточка одного прохода
          занимала пол-экрана: на четырёх агентах экран становился лентой, по
          которой не пробежать глазом.

          Подпись стадии убрана: она и так стоит в шапке прохода, а второй
          раз занимает строку.
        */}
        {/* Прибор — только у идущего прохода. У законченного скорость по
            определению ноль, и метка на нуле читалась бы как «встало». */}
        {run.state === 'running' && (
          <SpeedGauge
            read={easedRate}
            sent={easedRateSent}
            quiet={!live}
            matched={p.bytesRead > 0 && p.bytesDistinct != null
              ? 1 - p.bytesDistinct / p.bytesRead
              : p.chunksTotal > 0 ? 1 - p.chunksNew / p.chunksTotal : null}
            stored={p.bytesRead > 0 ? p.bytesUploaded / p.bytesRead : null}
          />
        )}
        {/*
          Производные величины рядом со стрелками, а не врозь. Каждая
          отвечает на свой вопрос, и все — из тех чисел, что у нас уже есть:
          больше ниоткуда взять их нельзя, а придумывать нельзя тем более.
        */}
        <div className="run__right">
          <div className="run__bar" role="img"
               aria-label={known ? `готово ${Math.round(share * 100)} процентов` : 'обход, доля пока неизвестна'}>
            {known ? (
              <span className="run__fill" style={{ width: `${Math.round(easedShare * 100)}%` }} />
            ) : (
              // На обходе знаменателя ещё нет. Полоса «на глаз» врала бы про
              // остаток, а бегущая штриховка честно значит «идёт, сколько —
              // неизвестно».
              <span className="run__fill run__fill--indeterminate" />
            )}
          </div>
          <dl className="run__stats">
      <div>
        <dt>файлов</dt>
        <dd>{silent ? <span className="muted">считает</span>
          : byFiles
            ? (
              <>
                {count(Math.round(easedFiles))} из {count(p.filesTotal!)}
                {/* Оценка оказалась меньше действительности — говорим об
                    этом, а не подгоняем числа под неё. */}
                {overrun && <span className="muted" title="Замер объёма сделан раньше прохода — файлов стало больше"> · оценка устарела</span>}
              </>
            )
            : <>{count(p.filesDone)}<span className="muted"> из ещё неизвестного числа</span></>}</dd>
      </div>
      <div>
        <dt>прочитано</dt>
        <dd>
          {silent ? <span className="muted">—</span> : bytes(easedRead)}
          {/*
            «≈» не украшение: для базы ожидаемое — оценка по
            information_schema, и она расходится с дампом в полтора раза.
            Показать её без оговорки значило бы обещать остаток, которого
            нет.
          */}
          {byBytes && !silent && (
            <span className="muted" title="Оценка размера базы, а не точный размер дампа">
              {' '}из ≈{bytes(p.bytesTotal!)}
            </span>
          )}
        </dd>
      </div>
      <div>
        {/*
          Ноль тут держится долго и не значит «ничего не происходит».
          Загрузка считается по ЗАКРЫТЫМ пакам, а пак закрывается на 64 МБ:
          пока первый набирается, прочитано растёт, а залито стоит на нуле.
          Прочерк в этом месте читается как поломка, поэтому говорим прямо,
          чего ждём.
        */}
        <dt>залито</dt>
        <dd>{p.bytesUploaded > 0
          ? bytes(p.bytesUploaded)
          : <span className="muted" title="Считается по закрытым пакам: пак закрывается на 64 МБ или в конце прохода">
              пак набирается
            </span>}</dd>
      </div>
      <div>
        <dt>чанков</dt>
        <dd>
          {p.chunksTotal > 0 || p.chunksNew > 0
            ? <>{count(p.chunksNew)} новых
                {p.chunksTotal > 0 && <span className="muted"> из {count(p.chunksTotal)}</span>}</>
            : <span className="muted">—</span>}
        </dd>
      </div>
          </dl>
          <dl className="derived">
          {/*
            «Совпало» считается по байтам, если агент прислал объём
            различного, и по чанкам, если нет. Второе — не то же самое:
            чанки разной длины, и доля по счёту отличается от доли по
            объёму. Поэтому подписываем, ПО ЧЕМУ считано, а не выдаём одно
            за другое.
          */}
          {/*
            «Совпало» показывается, только когда есть объём различного.
            Посчитанное по чанкам оно было бы пересказом соседней ячейки
            «чанков» другими словами: доля по счёту выводится из тех же двух
            чисел, что там и стоят. Повтор, поданный как отдельная величина,
            создаёт впечатление, что мы знаем больше, чем знаем.
          */}
          {p.bytesRead > 0 && p.bytesDistinct != null && (
            <div>
              <dt>совпало</dt>
              <dd title="Доля прочитанного по объёму, которого не пришлось отправлять">
                {Math.round((1 - p.bytesDistinct / p.bytesRead) * 100)}%
              </dd>
            </div>
          )}
          {p.bytesDistinct != null && p.bytesDistinct > 0 && p.bytesUploaded > 0 && (
            <div>
              <dt>сжатие</dt>
              <dd title="Во сколько раз различное ужалось при записи">
                {(p.bytesDistinct / p.bytesUploaded).toFixed(1)}×
              </dd>
            </div>
          )}
          <div>
            <dt>в хранилище</dt>
            <dd title="Какая часть прочитанного в итоге легла в хранилище">
              {p.bytesRead > 0
                ? `${Math.round((p.bytesUploaded / p.bytesRead) * 100)}%`
                : <span className="muted">—</span>}
            </dd>
          </div>
          <div>
            <dt>файлов в секунду</dt>
            {/*
              Сейчас, а не в среднем с начала. На вопрос, который задают, глядя
              на упавшую скорость — «встало или файлы мелкие», — среднее за
              проход не отвечает вовсе: у медиатеки участок крупных файлов и
              участок кэша отличаются в десятки раз. Размер файла рядом и есть
              ответ.
            */}
            <dd title={avgFilesPerSec != null
              ? `Сейчас, за последние секунды. С начала прохода в среднем ${count(Math.round(avgFilesPerSec))}`
              : 'Сейчас, за последние секунды'}>
              {recent ? count(Math.round(recent.files)) : <span className="muted">—</span>}
              {recent?.size != null && <span className="muted"> · по {bytes(recent.size)}</span>}
            </dd>
          </div>
          <div>
            <dt>в среднем</dt>
            <dd title="Средняя скорость чтения с начала прохода">
              {average ? `${bytes(average.read)}/с` : <span className="muted">—</span>}
            </dd>
          </div>
          </dl>
          {/*
            Подвал переехал вбок, к остальным числам. Он занимал строку во всю
            ширину под прибором, а справа от прибора пустовала половина
            карточки: высота росла там, где было место вширь.
          */}
          <div className="run__foot">
            {p.currentPath && <p className="run__path mono" title={p.currentPath}>{p.currentPath}</p>}
            {/*
              Признак жизни отдельно от полосы. Числа могут не меняться минутами
              на одном большом файле — это нормально; а молчание дольше срока
              значит, что проход вот-вот закроет сторож, и это уже не нормально.
            */}
            <p className={`run__pulse${stale ? ' run__pulse--stale' : ''}`}>
              последнее движение {agoPhrase(run.lastActivityAt)}
              {stale && ' — сторож закроет проход, если движения не будет'}
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}

function RunRow({ run }: { run: AgentRunView }): React.JSX.Element {
  const failed = run.state === 'failed';
  return (
    <li className={`runs__item runs__item--${run.state}`}>
      <span className="runs__when" title={new Date(run.startedAt).toLocaleString('ru-RU')}>
        {ago(run.startedAt)}
      </span>
      <span className="runs__site">{run.siteId}</span>
      <span className="runs__sources">{run.sources.join(', ') || '—'}</span>
      <span className="runs__result">
        {failed ? (run.error?.msg ?? 'сбой')
          : run.state === 'ok' ? `${bytes(run.progress.bytesUploaded)} за ${duration(run.startedAt, run.finishedAt)}`
          : run.state}
      </span>
      <span className="runs__reason">{REASON_WORD[run.reason]}</span>
    </li>
  );
}

/**
 * Что агент видит на машине — и кнопка завести это в настройках.
 *
 * После перехода к манифестам консоль раскладки не знает: где корень сайта,
 * какие источники, какие контейнеры. Отчёт — единственное место, где это
 * видно, и без него владелец заводит сайты вслепую, набирая путь руками.
 *
 * Заведение при этом остаётся НАЖАТИЕМ, а не следствием отчёта: список сайтов
 * это права, и появляться они должны по решению. Иначе переименованный на
 * машине каталог тихо менял бы то, что копируется.
 */
function ReportedSiteRow({ site, onAdopt, busy }: {
  site: ReportedSite;
  onAdopt: (siteId: string) => void;
  busy: boolean;
}): React.JSX.Element {
  const off = site.enabled === false;

  return (
    <li className={`rep${site.claimed ? ' rep--claimed' : ''}${off ? ' rep--off' : ''}`}>
      <div className="rep__head">
        <span className="rep__id">{site.id}</span>
        <span className="rep__root mono">{site.root}</span>

        {/*
          Откуда взята раскладка. «Догадка» — не то же, что «манифест»: на
          соседней машине догадка ошиблась на трёх проектах из четырёх, причём
          в сторону молчания. Показывать их одинаково нельзя.
        */}
        <span className={`rep__origin rep__origin--${site.origin === 'манифест' ? 'ok' : 'guess'}`}>
          {site.origin}
        </span>

        {off ? <span className="rep__state">выключен в манифесте хоста</span>
          : site.claimed ? <span className="rep__state rep__state--ok">заведён</span>
          : (
            <button
              type="button"
              className="button button--small"
              onClick={() => onAdopt(site.id)}
              disabled={busy}
              title={`Завести сайт с корнем ${site.root}`}
            >
              Завести
            </button>
          )}
      </div>

      <div className="rep__sources">
        {site.sources.map((src) => (
          <span key={src.id} className="rep__src" title={src.exclude?.join('\n')}>
            <b>{src.id}</b> <span className="muted">{src.kind}</span> · <span className="mono">{src.where}</span>
            {src.exclude && src.exclude.length > 0 && (
              <span className="muted"> · минус {src.exclude.length}</span>
            )}
          </span>
        ))}
        {site.sources.length === 0 && <span className="muted">источников не объявлено</span>}
      </div>

      {site.containers.length > 0 && (
        <div className="rep__containers">
          {site.containers.map((c) => (
            <span key={c.name} className="rep__cont" title={c.name}>{c.service}</span>
          ))}
        </div>
      )}

      {site.gitStatus && (
        <p className={`rep__git${site.gitStatus.status === 'ок' ? '' : ' rep__git--warn'}`}>
          git: {site.gitStatus.status} — {site.gitStatus.detail}
        </p>
      )}
    </li>
  );
}

/**
 * Какой код стоит на машине — и стоит ли на ней сегодняшний.
 *
 * Показываем коммит, а не номер версии. Номер у агента полгода держался на
 * `0.3.0` при сорока правках подряд, и по нему нельзя было ответить на
 * единственный вопрос, ради которого версию вообще смотрят: есть тут та самая
 * правка или нет.
 *
 * Красным по умолчанию не красим — по просьбе команды агента и по делу: машины
 * обновляются в разное время, и значок тревоги на каждой, кто на коммит
 * позади, через неделю перестанут замечать. Выделяем другое — машину, отставшую
 * ОТ ОСТАЛЬНЫХ: если два агента на свежей сборке, а третий на позавчерашней,
 * это уже повод посмотреть.
 */
function BuildLine({ report, odd }: {
  report: HostReportView;
  /** Соседи на свежей сборке, а эта машина — нет. */
  odd: boolean;
}): React.JSX.Element {
  const b = report.build;
  const word = b.kind === 'current' ? 'последняя'
    : b.kind === 'behind' ? `отстала на ${b.commits} ${plural(b.commits, 'коммит', 'коммита', 'коммитов')}`
    : b.kind === 'foreign' ? 'не из main'
    : b.kind === 'no-reference' ? 'эталон неизвестен'
    : 'сборку не сообщает';

  return (
    <span className={`build${odd ? ' build--odd' : ''}`}>
      {report.buildCommit && <code className="mono build__commit">{report.buildCommit.slice(0, 8)}</code>}
      <span className={`build__word build__word--${b.kind}`}>{word}</span>
      {report.buildDirty && (
        <span className="build__dirty" title="В каталоге агента есть несохранённые правки: развёрнуто не то, что лежит в репозитории">
          с правками на месте
        </span>
      )}
    </span>
  );
}

function AgentCard({
  agent, onRevoke, onAdopt, onSource, onSiteEnabled, onSiteRemove,
  onRunNow, onCancel, onRefresh, onRename, onVerify, onRestore, busy, oddBuild,
}: {
  agent: AgentView;
  /** Соседи на свежей сборке, а эта машина — нет. */
  oddBuild: boolean;
  onRevoke: (agent: AgentView) => void;
  onAdopt: (hostId: string, siteId: string) => void;
  onSource: (hostId: string, siteId: string, sourceId: string, enabled: boolean) => void;
  onSiteEnabled: (hostId: string, siteId: string, enabled: boolean) => void;
  onSiteRemove: (hostId: string, siteId: string) => void;
  onRunNow: (hostId: string, siteId: string) => void;
  onCancel: (runId: string, siteId: string) => void;
  onRefresh: (hostId: string) => void;
  onRename: (kid: string, label: string, note: string) => Promise<void>;
  onVerify: (hostId: string, siteId: string) => void;
  onRestore: (hostId: string, siteId: string) => void;
  busy: boolean;
}): React.JSX.Element {
  const revoked = agent.state === 'revoked';

  return (
    <article className={`agent${revoked ? ' agent--revoked' : ''}`}>
      <header className="agent__head">
        <span className={`agent__dot${agent.online ? ' agent__dot--live' : ''}`} aria-hidden="true" />
        <div className="agent__id">
          {/*
            Правится подпись, а не имя в DNS. Из DNS выведено имя ведра, и
            менять его задним числом нельзя: блокировка объектов задаётся при
            создании ведра, переименовать ведро с копиями невозможно. Поэтому
            имя машины показано отдельной строкой ниже и остаётся фактом.
          */}
          <h3 className="agent__host">
            <EditName
              objectKey={agent.kid}
              shown={agent.label || agent.host}
              {...(agent.label && agent.label !== agent.host ? { factName: agent.host } : {})}
              {...(agent.note ? { note: agent.note } : {})}
              busy={busy}
              onSave={onRename}
            />
          </h3>
          <span className="agent__meta mono">
            {agent.hostId} · {agent.kid} · {agent.fingerprint}
          </span>
          {/*
            DNS-имя рядом с ведром: замена точек на тире необратима, и по
            имени ведра не восстановить, где стояли точки. Подпись — не
            украшение, а единственное место, где это видно.
          */}
          <span className="agent__dns">
            {agent.dns} → ведро <span className="mono">{agent.bucket}</span>
          </span>
        </div>
        <div className="agent__state">
          {revoked ? (
            <span className="agent__revoked" title={agent.revokedReason ?? ''}>
              ключ отозван {agoPhrase(agent.revokedAt)}
            </span>
          ) : agent.online ? (
            <span className="agent__online">на связи</span>
          ) : (
            <span className="muted">видели {agoPhrase(agent.lastSeenAt)}</span>
          )}
          {/*
            Перечитать настройки — на случай, когда решение принято, а агент
            его почему-то не подхватил. Обычно уведомление уходит само, сразу
            после правки; кнопка нужна, чтобы не гадать, дошло ли.
          */}
          {!revoked && agent.online && (
            <button
              type="button"
              className="button button--ghost button--small"
              disabled={busy}
              onClick={() => onRefresh(agent.hostId)}
              title="Велеть агенту перечитать настройки немедленно"
            >
              Перечитать настройки
            </button>
          )}
          {!revoked && (
            <button
              type="button"
              className="button button--ghost button--danger"
              onClick={() => onRevoke(agent)}
            >
              Отозвать ключ
            </button>
          )}
        </div>
      </header>

      {agent.currentRun
        ? <RunProgress run={agent.currentRun} onCancel={onCancel} busy={busy} />
        : <p className="agent__idle">Проход не идёт.</p>}

      {/*
        Что агент видит на машине. Стоит ВЫШЕ заведённого: пока сайт не
        заведён, копий по нему не будет, и это первое, что стоит увидеть.
      */}
      {agent.report ? (
        <details className="agent__report" open={agent.sites.length === 0}>
          <summary>
            Агент видит на машине: {agent.report.sites.length}{' '}
            {agent.report.sites.length === 1 ? 'проект' : 'проектов'}
            {agent.report.sites.some((s) => !s.claimed && s.enabled !== false) && (
              <span className="agent__unclaimed"> · есть незаведённые</span>
            )}
            <span className="muted"> · осмотрено {agoPhrase(agent.report.collectedAt)}</span>
          </summary>

          <p className="agent__os muted">
            {agent.report.hostname} · {agent.report.os} · агент {agent.report.agentVersion}
            {' · '}
            <BuildLine report={agent.report} odd={oddBuild} />
          </p>

          <ul className="reps">
            {agent.report.sites.map((s) => (
              <ReportedSiteRow
                key={s.id}
                site={s}
                busy={busy}
                onAdopt={(siteId) => onAdopt(agent.hostId, siteId)}
              />
            ))}
          </ul>

          {agent.report.notSites.length > 0 && (
            <div className="agent__notsites">
              <b>Не копируется и почему:</b>
              <ul>
                {agent.report.notSites.map((n) => (
                  <li key={n.path}>
                    <span className="mono">{n.path}</span> — {n.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </details>
      ) : (
        <p className="agent__report-none muted">
          Отчёта о машине ещё не было: что на ней стоит — консоли неизвестно.
        </p>
      )}

      <div className="agent__sites">
        {agent.sites.length === 0 ? (
          // Не «пока не настроили», а молчащая машина, которая выглядит
          // работающей: агент подключён, а копий нет и не будет.
          <p className="alert alert--warn">
            Сайты не заведены — копий с этой машины не будет.
            {agent.report
              ? ' Заведите их кнопкой выше: корень возьмётся из отчёта агента.'
              : ' Дождитесь отчёта агента — он покажет, что на машине есть.'}
          </p>
        ) : (
          <ul className="sites">
            {agent.sites.map((s) => {
              // Что агент объявил про этот сайт: источники и их размеры.
              // Решать, копировать ли медиатеку, надо ЗНАЯ, что она весит
              // двести гигабайт, — а не узнав это утром по счёту за место.
              const declared = agent.report?.sites.find((r) => r.id === s.id);
              const known = new Set(s.sources.map((x) => x.id));
              // Один тип на оба случая: источник с решением владельца и
              // источник, который манифест объявил, а владелец ещё не трогал.
              // Второй копируется как объявлено — отсутствие решения это не
              // «выключено», и показывать их одинаково нельзя.
              const all: {
                id: string; enabled: boolean; decided: boolean;
                schedule?: string; verifyEveryNthRun?: number;
              }[] = [
                ...s.sources.map((x) => ({ ...x, decided: true })),
                ...(declared?.sources ?? [])
                  .filter((x) => !known.has(x.id))
                  .map((x) => ({ id: x.id, enabled: true, decided: false })),
              ];

              // Трогал ли владелец этот сайт хоть раз: от этого зависит,
              // новость ли необъявленный ранее источник.
              const anyDecided = s.sources.length > 0;

              // Сколько всего будет скопировано у этого сайта.
              //
              // Считается только по ВКЛЮЧЁННЫМ источникам: свёрнутая строка
              // должна отвечать на вопрос «сколько мы отсюда берём», а не
              // «сколько тут лежит». Медиатека, выключенная владельцем, в это
              // число входить не должна — иначе он увидит 230 ГБ там, где
              // копируется девятьсот мегабайт.
              const enabledIds = new Set(all.filter((x) => x.enabled).map((x) => x.id));
              const counted = (declared?.sources ?? []).filter((d) => enabledIds.has(d.id));
              const totals = counted.reduce(
                (acc, d) => ({
                  bytes: acc.bytes + (d.bytes ?? 0),
                  files: acc.files + (d.files ?? 0),
                  tables: acc.tables + (d.tables ?? 0),
                  measured: acc.measured || d.bytes != null,
                }),
                { bytes: 0, files: 0, tables: 0, measured: false },
              );
              const off = all.length - enabledIds.size;

              return (
                <li key={s.id} className={`sites__item${s.enabled ? '' : ' sites__item--off'}`}>
                  <div className="sites__head">
                    <span className="sites__name">{s.id}</span>
                    <span className="sites__root mono">{s.root}</span>
                    <span className="sites__last">
                      {s.lastSnapshotAt ? `снимок ${agoPhrase(s.lastSnapshotAt)}` : 'снимков ещё нет'}
                    </span>
                    {/*
                      «Снята» и «восстановима» — разные утверждения, и второе
                      весит больше. Поэтому исход проверки стоит тут же: иначе
                      первое незаметно подменяет второе.
                    */}
                    {/*
                      Что дала последняя копия. «Залито 83 МБ» само по себе не
                      говорит ничего: непонятно, мало это потому что данные
                      повторяются, или потому что проход только начался. Рядом
                      с прочитанным — говорит всё, и это тот вопрос, который
                      задаёт владелец: сколько мы сэкономили.
                    */}
                    {s.lastTotals && s.lastTotals.bytes > 0 && (
                      <span className="sites__gain">
                        {bytes(s.lastTotals.bytes)} → {bytes(s.lastTotals.uploadedBytes)}
                        {s.lastTotals.newBytes != null && s.lastTotals.bytes > 0 && (
                          <span className="sites__gain-pct">
                            {' '}−{Math.round((1 - s.lastTotals.newBytes / s.lastTotals.bytes) * 100)}%
                          </span>
                        )}
                      </span>
                    )}
                    {s.verify && (
                      <span className={`sites__verify sites__verify--${s.verify.state}`}
                            title={s.verify.detail ?? ''}>
                        {s.verify.state === 'queued' && 'проверка в очереди'}
                        {s.verify.state === 'running'
                          && `проверяется ${s.verify.total > 0
                            ? `${Math.round((s.verify.checked / s.verify.total) * 100)}%`
                            : '…'}`}
                        {s.verify.state === 'ok' && `восстановима · ${agoPhrase(s.verify.finishedAt)}`}
                        {s.verify.state === 'failed' && 'ВОССТАНОВИТЬ НЕЛЬЗЯ'}
                      </span>
                    )}
                    <span className="sites__keep">хранить {s.retentionDays} дн.</span>

                    <span className="sites__actions">
                      {/*
                        Выключить и убрать — разные вещи, и обе нужны.
                        Выключенный остаётся в настройках и виден как решение;
                        убранный исчезает, и вернуть его надо будет заведением.
                        Для «пока не копируем» правильнее первое.
                      */}
                      {/*
                        «Скопировать сейчас» — первой и не призрачной: это то,
                        ради чего экран открывают чаще всего. Гасится, когда
                        агент не на связи или сайт выключен: кнопка, которая
                        отвечает отказом, хуже кнопки, которой нет.
                      */}
                      <button
                        type="button"
                        className="button button--small"
                        disabled={busy || !agent.online || !s.enabled}
                        onClick={() => onRunNow(agent.hostId, s.id)}
                        title={!agent.online
                          ? 'Агент не на связи — команду отправить некому'
                          : !s.enabled
                            ? 'Сайт выключен: включите копирование, если копия нужна'
                            : 'Начать проход, не дожидаясь расписания'}
                      >
                        Скопировать сейчас
                      </button>
                      {/*
                        Сайт целиком — тем же переключателем, что и его
                        источники: это одно и то же решение, только уровнем
                        выше. Кнопка «Выключить» рядом с переключателями
                        источников выглядела как действие другого рода.
                      */}
                      <Toggle
                        checked={s.enabled}
                        disabled={busy}
                        label="копировать"
                        ariaLabel={`Копировать сайт ${s.id}`}
                        title={s.enabled
                          ? 'Копируется. Выключить — копии прекратятся, настройки останутся'
                          : 'Не копируется по решению владельца'}
                        onChange={(next) => onSiteEnabled(agent.hostId, s.id, next)}
                      />
                      {/*
                        Проверка и восстановление — рядом с копированием, а не
                        на отдельном экране. Копия, из которой ни разу не
                        восстанавливались, — надежда, а не копия, и путь к
                        проверке должен быть в один щелчок оттуда, где на копию
                        смотрят.
                      */}
                      <button
                        type="button"
                        className="button button--ghost button--small"
                        disabled={busy || !s.lastSnapshotAt}
                        onClick={() => onVerify(agent.hostId, s.id)}
                        title={s.lastSnapshotAt
                          ? 'Прочитать все паки снимка и сверить каждый чанк по SHA-256'
                          : 'Снимков ещё нет — проверять нечего'}
                      >
                        Проверить копию
                      </button>
                      <button
                        type="button"
                        className="button button--ghost button--small"
                        disabled={busy || !s.lastSnapshotAt}
                        onClick={() => onRestore(agent.hostId, s.id)}
                        title={s.lastSnapshotAt
                          ? 'Выдать временные ключи на чтение и показать команду'
                          : 'Снимков ещё нет — восстанавливать нечего'}
                      >
                        Восстановить
                      </button>
                      <button
                        type="button"
                        className="button button--ghost button--small button--danger"
                        disabled={busy}
                        onClick={() => onSiteRemove(agent.hostId, s.id)}
                        title="Убрать из настроек. Снимки и паки останутся в хранилище"
                      >
                        Убрать
                      </button>
                    </span>
                  </div>

                  {/*
                    Источники сворачиваются, как и список проектов машины. На
                    машине с пятью сайтами по четыре источника экран иначе
                    превращается в двадцать строк, среди которых не найти ту
                    одну, где что-то не так.

                    В свёрнутом виде — то, ради чего сюда заходят: сколько
                    отсюда берём и из скольких файлов.
                  */}
                  <details className="srcs__wrap">
                    <summary className="srcs__summary">
                      <span className="srcs__count">
                        {all.length} {all.length === 1 ? 'источник' : 'источников'}
                        {off > 0 && <span className="muted"> · {off} выключено</span>}
                      </span>
                      {/*
                        Итог сразу за счётчиком, а не у правого края: на широком
                        экране прижатое к краю число оказывается в полуметре от
                        того, к чему относится, и читается как чужое.
                      */}
                      {totals.measured ? (
                        <span className="srcs__totals">
                          {bytes(totals.bytes)}
                          {totals.files > 0 && <span className="muted"> · {count(totals.files)} файлов</span>}
                          {totals.tables > 0 && <span className="muted"> · {count(totals.tables)} таблиц</span>}
                        </span>
                      ) : (
                        <span className="muted">не мерился</span>
                      )}
                    </summary>
                  <ul className="srcs">
                    {all.map((src) => {
                      const d = declared?.sources.find((x) => x.id === src.id);
                      return (
                        <li key={src.id} className={`srcs__item${src.enabled ? '' : ' srcs__item--off'}`}>
                          {/*
                            Переключатель, а не галочка: каждое движение здесь
                            уходит на сервер сразу и меняет то, что попадёт в
                            копию ночью. Галочка обещает «отмечено, применю
                            потом» — обещание, которого мы не выполняем.
                          */}
                          <Toggle
                            checked={src.enabled}
                            disabled={busy}
                            label={<span className="srcs__id">{src.id}</span>}
                            ariaLabel={`Копировать источник ${src.id}`}
                            title={src.enabled
                              ? 'Копируется. Выключить — источник останется в настройках'
                              : 'Не копируется по решению владельца'}
                            onChange={(next) => onSource(agent.hostId, s.id, src.id, next)}
                          />

                          {/*
                            Ячейка рисуется ВСЕГДА, даже пустая. Пропущенная
                            ячейка в сетке сдвигает всё, что за ней, и строки
                            перестают стоять колонками — ровно та беда, ради
                            которой сетка и заведена.
                          */}
                          <span className="srcs__where mono" title={d?.where ?? ''}>
                            {d?.where ?? ''}
                          </span>

                          {/*
                            Размер и число файлов. Пусто — не «ноль», а «агент
                            ещё не мерил»: показывать одно вместо другого
                            значит соврать в сторону спокойствия.
                          */}
                          {/*
                            «Не мерился» вместо «неизвестен». Разница не в
                            вежливости: агент меряет то, что заведено, а
                            незаведённое не трогает — и «неизвестен» звучит как
                            «мы смотрели и не смогли», хотя никто не смотрел.

                            Объём и счёт — РАЗНЫЕ ячейки: иначе цифры съезжают
                            друг относительно друга, и глазом их не сравнить,
                            а сравнивают их именно глазом, сверху вниз.
                          */}
                          <span className="srcs__size">
                            {d?.bytes != null
                              ? bytes(d.bytes)
                              : (
                                <span className="muted" title="Агент меряет источники заведённых сайтов; этот ещё не мерился">
                                  не мерился
                                </span>
                              )}
                          </span>
                          <span className="srcs__count">
                            {/*
                              У базы приходят таблицы, у файлового источника —
                              файлы. Если однажды придёт и то и другое, разделим
                              точкой, а не склеим в «10 таблиц92 файлов».
                            */}
                            {[
                              d?.files != null ? `${count(d.files)} файлов` : null,
                              d?.tables != null ? `${count(d.tables)} таблиц` : null,
                            ].filter(Boolean).join(' · ')}
                          </span>

                          <span className="srcs__tail">
                            {src.schedule && (
                              <span className="srcs__sched mono">{src.schedule}</span>
                            )}
                          {/*
                            Пометка только там, где она что-то значит.

                            Пока владелец не трогал сайт вовсе, «решения нет»
                            стоит у каждой строки и не говорит ничего: галочка
                            уже показывает, что источник копируется. А вот
                            источник, появившийся ПОСЛЕ настройки, — новость:
                            он попадёт в копию, и владелец про него не знал.
                          */}
                            {!src.decided && anyDecided && (
                              <span className="srcs__new" title="Источник появился после настройки — копируется как объявлено">
                                новый
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                    {all.length === 0 && (
                      <li className="muted">Источников агент не объявил.</li>
                    )}
                  </ul>
                  </details>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {agent.recentRuns.length > 0 && (
        <details className="agent__history">
          <summary>Последние проходы ({agent.recentRuns.length})</summary>
          <ul className="runs">
            {agent.recentRuns.map((r) => <RunRow key={r.runId} run={r} />)}
          </ul>
        </details>
      )}
    </article>
  );
}

export function Agents(): React.JSX.Element {
  const { data, error, loading, reload } = useQuery<AgentsSnapshot>('agents.list');
  const live = useTopic<AgentEvent>(AGENTS_TOPIC);
  const [snapshot, setSnapshot] = useState<AgentsSnapshot | null>(null);
  const [issued, setIssued] = useState<IssuedTokenView | null>(null);
  const [busy, setBusy] = useState(false);
  const { pending, ask, close } = useConfirm();
  const [restore, setRestore] = useState<{
    restoreId: string; siteId: string; snapshotId: string; endpoint: string;
    bucket: string; accessKeyId: string; secretAccessKey: string; expiresAt: string;
  } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => { if (data) setSnapshot(data); }, [data]);

  // Живое событие меняет карточку на месте, а не перезапрашивает список: на
  // прогрессе события идут секундами, и запрос на каждое залил бы сервер.
  useEffect(() => {
    const e = live.last;
    if (!e) return;

    if (e.kind === 'agent.changed') {
      reload();
      return;
    }

    // Связь появилась или пропала. Отдельно от проходов: карточку меняем в
    // одном месте, а не гадаем по признакам чужого события.
    if (e.kind === 'agent.presence') {
      setSnapshot((prev) => prev && {
        ...prev,
        agents: prev.agents.map((a) => (a.kid === e.kid ? { ...a, online: e.online } : a)),
      });
      return;
    }

    setSnapshot((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        agents: prev.agents.map((a) => {
          if (a.hostId !== e.hostId) return a;
          if (e.kind === 'run.finished') {
            return {
              ...a,
              currentRun: null,
              // Завершённый проход сразу становится верхним в истории, а не
              // ждёт следующего запроса: иначе он исчезает с экрана совсем и
              // выглядит как «ничего не было».
              recentRuns: [e.run, ...a.recentRuns].slice(0, 5),
            };
          }
          return { ...a, currentRun: e.run, online: true };
        }),
      };
    });
  }, [live.seq]);

  const issue = async (): Promise<void> => {
    // Спрашиваем DNS-имя, а не «как называется машина»: из него ВЫВОДИТСЯ имя
    // ведра, и решается это здесь, один раз. Переименовать ведро в S3 нельзя —
    // есть только перенос содержимого, а ведро под блокировкой не переносится
    // до конца срока хранения.
    const dns = prompt(
      'DNS-имя машины — настоящее или условное.\n\n' +
      'Например: docker.helsinki.eu-brands.de\n\n' +
      'Из него выведется имя ведра заменой точек на тире. Изменить его потом\n' +
      'будет нельзя: вёдра в S3 не переименовываются.',
    );
    if (!dns) return;
    setBusy(true);
    setFailure(null);
    try {
      setIssued(await request<IssuedTokenView>('agents.enroll', { dns: dns.trim() }));
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось выдать код');
    } finally {
      setBusy(false);
    }
  };

  const reinstall = async (hostId: string, host: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      setIssued(await request<IssuedTokenView>('agents.enroll', { dns: host, hostId }));
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось выдать код');
    } finally {
      setBusy(false);
    }
  };

  const adopt = async (hostId: string, siteId: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      const res = await request<{ root: string }>('agent.site.adopt', { hostId, siteId });
      // Корень берётся из отчёта и показывается в подтверждении: набирать его
      // руками — верный способ ошибиться там, где ошибка не видна.
      setNotice(`Сайт ${siteId} заведён, корень ${res.root}. Агент перечитает настройки при следующем подключении.`);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось завести сайт');
    } finally {
      setBusy(false);
    }
  };

  const setSource = async (
    hostId: string, siteId: string, sourceId: string, enabled: boolean,
  ): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      // Источник ВЫКЛЮЧАЕТСЯ признаком, а не исчезает из списка: манифест на
      // машине его всё равно объявляет, и отсутствие записи означало бы
      // «копировать как объявлено».
      await request('agent.site.policy', {
        hostId, siteId, source: { id: sourceId, enabled },
      });
      setNotice(`${sourceId}: ${enabled ? 'копировать' : 'НЕ копировать'}`);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось изменить');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Скопировать сейчас.
   *
   * Ответ говорит «команда отправлена», а не «проход пошёл», и на экране
   * написано так же. Начало прохода объявляет агент — до этого показывать
   * идущую копию значило бы обещать то, чего может не случиться.
   */
  const runNow = async (hostId: string, siteId: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await request('agents.run-now', { hostId, siteId });
      setNotice(`${siteId}: команда отправлена, ждём начала прохода`);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось запустить');
    } finally {
      setBusy(false);
    }
  };

  const cancelRun = async (runId: string, siteId: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await request('agents.cancel', { runId });
      setNotice(`${siteId}: остановка запрошена, проход закроет агент`);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось остановить');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Поставить последний снимок сайта на проверку восстановимости.
   *
   * Отвечаем «поставлен», а не «проверен»: проверка читает ВСЕ паки снимка, и
   * на медиатеке это минуты. Обещать результат сразу значило бы соврать в
   * сторону спокойствия.
   */
  const verifySite = async (hostId: string, siteId: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await request('snapshot.verify', { hostId, siteId });
      setNotice(`${siteId}: проверка поставлена в очередь, исход появится здесь же`);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось поставить проверку');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Выдать ключи на чтение снимка.
   *
   * Секрет показывается ОДИН раз и у нас не хранится — как код регистрации.
   * Поэтому он выводится сразу с готовой командой: переспросить будет негде.
   */
  const grantRestore = async (hostId: string, siteId: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      const list = await request('snapshot.list', { hostId, siteId, limit: 1 }) as {
        snapshots: { snapshotId: string; state: string }[];
      };
      const latest = list.snapshots.find((x) => x.state === 'complete');
      if (!latest) throw { msg: `у сайта ${siteId} нет готовых снимков` };

      const g = await request('restore.grant', { snapshotId: latest.snapshotId }) as {
        restoreId: string; endpoint: string; bucket: string;
        accessKeyId: string; secretAccessKey: string; expiresAt: string;
      };
      setRestore({ ...g, siteId, snapshotId: latest.snapshotId });
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось выдать ключи');
    } finally {
      setBusy(false);
    }
  };

  const renameAgent = async (kid: string, label: string, note: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await request('agents.rename', { kid, label, note });
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось переименовать');
    } finally {
      setBusy(false);
    }
  };

  const refreshAgent = async (hostId: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      const r = await request('agents.refresh', { hostId }) as { version?: number };
      setNotice(`настройки версии ${r?.version ?? '?'} отправлены агенту`);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Опасное — только через вопрос.
   *
   * Обёртки отделены от самих действий намеренно: действие знает, ЧТО делает,
   * а обёртка — чем это грозит. Смешивать их значит однажды добавить кнопку и
   * забыть про вопрос.
   */
  const askRevoke = (agent: AgentView): void => {
    ask({
      title: `Отозвать ключ агента ${agent.dns || agent.host}?`,
      consequences: [
        'Агент перестанет присылать копии сразу же — соединение разорвётся, идущий проход оборвётся.',
        'Чтобы вернуть машину в работу, придётся регистрировать агента заново: выдать код, поставить ключ на машине.',
        'Уже снятые копии останутся: ключ отзывается, ведро и снимки не трогаются.',
      ],
      instead: 'Если машину нужно временно исключить из копирования — выключите её сайты, ключ при этом останется.',
      confirmLabel: 'Отозвать ключ',
      typeToConfirm: agent.dns || agent.host,
      run: () => revoke(agent.kid, 'отозван владельцем из консоли'),
    });
  };

  const askRemoveSite = (hostId: string, siteId: string): void => {
    ask({
      title: `Убрать ${siteId} из копирования?`,
      consequences: [
        'Новых копий по этому сайту не будет: агент перестанет его обходить.',
        'Снимки и паки останутся в хранилище — удалять их отсюда консоль не умеет.',
        'Завести сайт заново под тем же именем можно в любой момент, и история продолжится: идентификатор не меняется.',
      ],
      instead: 'Если нужно просто приостановить — переключателем «копировать», настройки при этом сохранятся.',
      confirmLabel: 'Убрать из копирования',
      run: () => removeSite(hostId, siteId),
    });
  };

  const askCancel = (runId: string, siteId: string): void => {
    ask({
      title: `Остановить проход по ${siteId}?`,
      consequences: [
        'Агент прекратит чтение и закроет проход как неудавшийся — снимка не будет.',
        'Уже залитые паки останутся в хранилище и пригодятся следующему проходу: чанки в них не пропадут.',
        'Запустить заново можно сразу же.',
      ],
      confirmLabel: 'Остановить',
      run: () => cancelRun(runId, siteId),
    });
  };

  const setSiteEnabled = async (
    hostId: string, siteId: string, enabled: boolean,
  ): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await request('agent.site.policy', { hostId, siteId, enabled });
      setNotice(enabled
        ? `${siteId}: копирование включено`
        : `${siteId}: копирование выключено, настройки сохранены`);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось изменить');
    } finally {
      setBusy(false);
    }
  };

  const removeSite = async (hostId: string, siteId: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      const res = await request<{ snapshots: number }>('agent.site.remove', { hostId, siteId });
      setNotice(
        `${siteId} убран из копирования.` +
        (res.snapshots > 0 ? ` ${res.snapshots} снимков остались в хранилище.` : ''),
      );
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось убрать');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (kid: string, reason: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await request('agents.revoke', { kid, reason });
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось отозвать');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Агенты</h2>
        <p className="alert alert--error">Ошибка: {error.msg}</p>
      </section>
    );
  }

  const view = snapshot;

  /*
   * Кто отстал ОТ ОСТАЛЬНЫХ, а не от эталона.
   *
   * Просто «отстала на коммит» — не новость: машины обновляются в разное время.
   * Новость — когда соседи уже на свежей сборке, а эта нет: значит обновление
   * прошло мимо неё, и это единственный случай, ради которого стоит привлекать
   * внимание. Считаем только по машинам, которые вообще шлют отпечаток:
   * молчащая сборка ничего не говорит ни о ней, ни о соседях.
   */
  const laggards = (() => {
    const out = new Set<string>();
    const known = (view?.agents ?? []).filter(
      (a) => a.report && a.report.build.kind !== 'silent' && a.report.build.kind !== 'no-reference',
    );
    if (!known.some((a) => a.report!.build.kind === 'current')) return out;
    for (const a of known) if (a.report!.build.kind !== 'current') out.add(a.kid);
    return out;
  })();
  const running = view?.agents.filter((a) => a.currentRun).length ?? 0;

  return (
    <section className="panel panel--fill">
      {/*
        Вопрос один на экран, а не по флагу на кнопку: иначе новая опасная
        кнопка появится без вопроса, и никто этого не заметит.
      */}
      {/*
        Ключи на чтение показываются ОДИН раз: секрет у нас не хранится, как и
        код регистрации. Поэтому рядом сразу готовая команда — переспросить
        будет негде.
      */}
      {restore && (
        <div className="modal" role="presentation" onClick={() => setRestore(null)}>
          <div className="modal__box modal__box--wide" role="dialog" aria-modal="true"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="modal__title">Ключи на чтение снимка {restore.siteId}</h3>
            <p className="modal__line">
              Годны до {new Date(restore.expiresAt).toLocaleString('ru-RU')}. Только чтение
              этого снимка и паков его машины: записать или удалить ими нельзя.
            </p>
            <p className="modal__line muted">
              Секрет показывается один раз — мы его не храним. Восстанавливать лучше
              не на машине сайта: разворачивать архив после взлома на той же машине незачем.
            </p>
            <pre className="modal__code">{[
              `export AWS_ACCESS_KEY_ID=${restore.accessKeyId}`,
              `export AWS_SECRET_ACCESS_KEY=${restore.secretAccessKey}`,
              '',
              `mc alias set restore ${restore.endpoint} "$AWS_ACCESS_KEY_ID" "$AWS_SECRET_ACCESS_KEY"`,
              `mc ls --recursive restore/${restore.bucket}/sites/${restore.siteId}/snapshots/${restore.snapshotId}/`,
            ].join('\n')}</pre>
            <div className="modal__actions">
              <button type="button" className="button button--ghost"
                      onClick={() => { void request('restore.revoke', { restoreId: restore.restoreId }); setRestore(null); }}>
                Закрыть окно доступа
              </button>
              <button type="button" className="button" onClick={() => setRestore(null)}>
                Готово
              </button>
            </div>
          </div>
        </div>
      )}

      <Confirm
        open={pending !== null}
        title={pending?.title ?? ''}
        consequences={pending?.consequences ?? []}
        {...(pending?.instead ? { instead: pending.instead } : {})}
        confirmLabel={pending?.confirmLabel ?? 'Подтвердить'}
        {...(pending?.typeToConfirm ? { typeToConfirm: pending.typeToConfirm } : {})}
        busy={busy}
        onCancel={close}
        onConfirm={() => { const run = pending?.run; close(); void run?.(); }}
      />
      <header className="panel__head">
        <h2 className="panel__title">Агенты</h2>
        {view && <TotalSpeed agents={view.agents} />}
        <div className="agents__bar">
          <p className="muted">
            {view
              ? `${view.agents.length} агентов, на связи ${view.agents.filter((a) => a.online).length}` +
                (running > 0 ? `, идёт проходов: ${running}` : '')
              : loading ? 'Спрашиваем…' : ''}
          </p>
          <button type="button" className="button" onClick={issue} disabled={busy}>
            Завести агента
          </button>
        </div>

        {failure && <p className="alert alert--error">{failure}</p>}
        {notice && (
          <p className="alert alert--ok" onClick={() => setNotice(null)}>{notice}</p>
        )}

        {issued && (
          <div className="token">
            <p className="token__lead">
              {issued.reinstall
                ? `Код на переустановку агента хоста ${issued.hostId}.`
                : `Код регистрации нового агента. Хост ${issued.hostId}.`}{' '}
              Годен до {new Date(issued.expiresAt).toLocaleString('ru-RU')}.
            </p>
            <p className="token__bucket">
              {/*
                Имя ведра показывается СРАЗУ, до того как код унесут на машину:
                это единственный момент, когда его ещё можно изменить. Дальше
                остаётся только перенос содержимого, а ведро под блокировкой не
                переносится до конца срока хранения.
              */}
              <span className="mono">{issued.dns}</span> → ведро{' '}
              <b className="mono">{issued.bucket}</b>
            </p>
            <code className="token__value mono">{issued.token}</code>
            <p className="token__warn">
              Показывается один раз: в базе лежит только хеш, и второй раз консоль его
              не покажет — не из строгости, а потому что не может. На машине положить в{' '}
              <code className="mono">EBK_ENROLL_TOKEN</code>, запустить агента и сразу
              удалить строку.
            </p>
            <button type="button" className="button button--ghost" onClick={() => setIssued(null)}>
              Записал, убрать
            </button>
          </div>
        )}
      </header>

      <div className="panel__scroll">
        {!view ? (
          <p className="muted">{loading ? 'Спрашиваем…' : 'Нет данных.'}</p>
        ) : view.agents.length === 0 ? (
          <p className="muted">
            Агентов ещё нет. «Завести агента» выдаст разовый код — его нужно донести до
            машины и положить в <code className="mono">EBK_ENROLL_TOKEN</code>.
          </p>
        ) : (
          <div className="agents">
            {view.agents.map((a) => (
              <AgentCard
                key={a.kid} agent={a} onRevoke={askRevoke} onAdopt={adopt}
                oddBuild={laggards.has(a.kid)}
                onSource={setSource} onSiteEnabled={setSiteEnabled}
                onSiteRemove={askRemoveSite} busy={busy}
                onRunNow={runNow} onCancel={askCancel} onRefresh={refreshAgent}
                onRename={renameAgent} onVerify={verifySite} onRestore={grantRestore}
              />
            ))}
          </div>
        )}

        {view && view.pendingTokens.length > 0 && (
          <section className="tokens">
            <h3 className="tokens__title">Коды выданы, но не использованы</h3>
            <ul className="tokens__list">
              {view.pendingTokens.map((t) => (
                <li key={t.hostId + t.createdAt}>
                  <span className="tokens__label">{t.dns}</span>
                  <span className="mono">→ {t.bucket}</span>
                  <span className="muted">
                    {t.reinstall ? 'переустановка' : 'новая машина'} · годен ещё {until(t.expiresAt)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {view && view.agents.some((a) => a.state === 'revoked') && (
          <p className="muted agents__note">
            Отозванный ключ не оживает. Чтобы поставить агента на ту же машину заново,
            выдайте код на её прежний hostId — тогда область дедупликации сохранится и
            первый проход не станет полным.
          </p>
        )}
      </div>

      <footer className="panel__foot">
        <span className="legend__item">полоса считается по файлам, не по байтам</span>
        {live.gap && <span className="legend__item">часть событий потерялась при обрыве</span>}
        <span className="panel__stamp">
          {view ? `обновлено ${new Date(view.generatedAt).toLocaleTimeString('ru-RU')}` : ''}
        </span>
      </footer>
    </section>
  );
}
